import React,{ memo, useState, useCallback, useRef } from 'react';
import { View, Text, TouchableOpacity, StyleSheet } from 'react-native';
import { Entypo } from '@expo/vector-icons';
import { fontFamily } from '../../../helpers/fontFamily';

const BulkActionDropdown = memo(({
  label,
  options = [],
  onSelect,
  disabled = false,
  testID,
  isOpen: controlledIsOpen,
  onToggle,
  selectedCount = 0
}) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const buttonRef = useRef(null);

  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
  const setIsOpen = onToggle || setInternalIsOpen;

  const handleToggle = useCallback(() => {
    if (!disabled) {
      setIsOpen(prev => !prev);
    }
  }, [disabled]);

  const handleSelect = useCallback((option) => {
    setIsOpen(false);
    onSelect?.(option);
  }, [onSelect]);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  return (
    <View style={styles.container}>
      <TouchableOpacity
        ref={buttonRef}
        style={styles.dropdownButton}
        onPress={handleToggle}
        disabled={disabled}
        testID={testID}
      >
        <View style={styles.buttonContent}>
          <Text style={[styles.buttonText, disabled && styles.buttonTextDisabled]}>
            {label}
          </Text>
          <Entypo
            name="chevron-down"
            size={18}
            color={disabled ? '#666' : '#fff'}
            style={styles.chevronIcon}
          />
        </View>
      </TouchableOpacity>

      {/* Dropdown menu - positioned relative to button */}
      {isOpen && (
        <>
          {/* Invisible overlay to close dropdown when clicking outside */}
          <TouchableOpacity
            style={styles.overlay}
            activeOpacity={1}
            onPress={handleClose}
          />
          {/* Dropdown menu anchored below button */}
          <View style={styles.dropdownMenu}>
            {options.map((item) => (
              <TouchableOpacity
                key={item.value}
                style={styles.optionItem}
                onPress={() => handleSelect(item)}
                testID={`${testID}-option-${item.value}`}
              >
                <View style={styles.optionRow}>
                  <Text style={styles.countText}>{selectedCount}</Text>
                  <Text style={styles.optionText}>{item.label}</Text>
                </View>
              </TouchableOpacity>
            ))}
          </View>
        </>
      )}
    </View>
  );
});

BulkActionDropdown.displayName = 'BulkActionDropdown';

const styles = StyleSheet.create({
  container: {
    position: 'relative',
    zIndex: 99999
  },
  dropdownButton: {
    paddingHorizontal: 6
  },
  buttonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: '#ddd'
  },
  buttonText: {
    color: '#fff',
    fontFamily: fontFamily.font300,
    fontSize: 14
  },
  buttonTextDisabled: {
    color: '#666'
  },
  chevronIcon: {
    marginLeft: 3
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 99998
  },
  dropdownMenu: {
    position: 'absolute',
    top: '100%',
    right: 0,
    marginTop: 5,
    backgroundColor: 'rgb(38, 43, 48)',
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#80808082',
    width: '12rem',
    zIndex: 99999
  },
  optionItem: {
    padding: 10
  },
  optionRow: {
    flexDirection: 'row',
    alignItems: 'center'
  },
  countText: {
    color: '#fff',
    marginRight: 16,
    minWidth: 16,
    fontFamily: fontFamily.font300,
    fontSize: 14
  },
  optionText: {
    color: '#fff',
    fontFamily: fontFamily.font300,
    fontSize: 14
  }
});

export default BulkActionDropdown;

